import { memo, useMemo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import styles from "./SchedulePreviewTable.module.scss";

export interface SchedulePreviewColumn {
    id: string;
    header: string;
    headerTooltip?: string;
    isNumeric?: boolean;
    minWidth?: number;
    maxWidth?: number;
}

export interface SchedulePreviewRow {
    id: string;
    values: Record<string, string>;
}

interface SchedulePreviewTableProps {
    columns: SchedulePreviewColumn[];
    rows: SchedulePreviewRow[];
    className?: string;
}

function makePreviewCell(column: SchedulePreviewColumn) {
    const PreviewCell = memo(function SchedulePreviewCell({
        row,
    }: CellContext<ViewTableFeatures, SchedulePreviewRow, unknown>) {
        return (
            <ViewTableCell
                className={classNames(
                    column.isNumeric ? styles.numericCell : undefined,
                )}
            >
                {row.original.values[column.id]}
            </ViewTableCell>
        );
    });
    return PreviewCell;
}

function makePreviewHeader(column: SchedulePreviewColumn) {
    if (column.headerTooltip === undefined) {
        return undefined;
    }
    const PreviewHeader = () => (
        <Tooltip content={column.headerTooltip} side="bottom">
            <span>{column.header}</span>
        </Tooltip>
    );
    return PreviewHeader;
}

function buildPreviewConfig(
    columns: SchedulePreviewColumn[],
): ViewConfig<SchedulePreviewRow, string, string> {
    const configColumns: ViewConfig<
        SchedulePreviewRow,
        string,
        string
    >["columns"] = {};
    for (const column of columns) {
        configColumns[column.id] = {
            name: column.header,
            header: column.header,
            accessorKey: "id",
            cell: makePreviewCell(column),
            headerCell: makePreviewHeader(column),
            minSize: column.minWidth ?? 90,
            maxSize: column.maxWidth,
            align: column.isNumeric ? "right" : undefined,
            immutable: true,
            hiddenFromSettings: true,
        };
    }
    return { columns: configColumns };
}

function buildPreviewView(columns: SchedulePreviewColumn[]): View {
    return {
        type: ViewType.SCHEDULE_PREVIEW,
        columns: columns.map((column) => ({ id: column.id })),
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.SM,
        },
    };
}

// Ephemeral read-only recognition-ladder table shared by the apply modal and
// the template drawer previews; the view literal is never persisted.
export const SchedulePreviewTable: React.FC<SchedulePreviewTableProps> = ({
    columns,
    rows,
    className,
}) => {
    const view = useMemo(() => buildPreviewView(columns), [columns]);
    const config = useMemo(() => buildPreviewConfig(columns), [columns]);

    return (
        <ViewTable
            data={rows}
            view={view}
            config={config}
            className={className}
            rowBorders={false}
        />
    );
};
